import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, FolderKanban, X } from 'lucide-react'
import { projectApi } from '../services/projectApi'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { EmptyState } from '../components/common/EmptyState'
import { StatusBadge } from '../components/common/StatusBadge'
import { ProgressBar } from '../components/common/ProgressBar'
import type { Project, ProjectCreate, ProjectStatus } from '../types'

export function ProjectList() {
  const navigate = useNavigate()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | ''>('')
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [formData, setFormData] = useState<ProjectCreate>({
    projectNumber: '',
    name: '',
    client: '',
    description: '',
    status: 'Draft',
    location: '',
    startDate: new Date().toISOString().split('T')[0]!,
  })

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await projectApi.getAll({
        search: search || undefined,
        status: statusFilter || undefined,
      })
      setProjects(result.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects')
    } finally {
      setLoading(false)
    }
  }, [search, statusFilter])

  useEffect(() => {
    loadProjects()
  }, [loadProjects])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    try {
      setCreating(true)
      const project = await projectApi.create(formData)
      setShowCreate(false)
      setFormData({
        projectNumber: '',
        name: '',
        client: '',
        description: '',
        status: 'Draft',
        location: '',
        startDate: new Date().toISOString().split('T')[0]!,
      })
      navigate(`/projects/${project.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="page-container animate-fade-in">
      <div className="page-header">
        <div>
          <h1>Projects</h1>
          <p className="page-subtitle">Manage commissioning projects</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} />
            New Project
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <div className="input-group" style={{ flex: '1', maxWidth: '360px' }}>
          <Search size={16} className="input-icon" />
          <input
            type="text"
            className="form-input search-input"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="form-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ProjectStatus | '')}
        >
          <option value="">All Statuses</option>
          <option value="Draft">Draft</option>
          <option value="Active">Active</option>
          <option value="OnHold">On Hold</option>
          <option value="Completed">Completed</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      </div>

      {error && (
        <div className="info-card danger mb-4">
          <p style={{ margin: 0, color: 'var(--accent-red)' }}>{error}</p>
        </div>
      )}

      {loading ? (
        <LoadingSpinner message="Loading projects..." />
      ) : projects.length === 0 ? (
        <EmptyState
          icon={<FolderKanban size={48} />}
          title="No projects found"
          message={search || statusFilter ? 'Try adjusting your search or filters.' : 'Create your first commissioning project to get started.'}
          action={
            !search && !statusFilter ? (
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                <Plus size={16} />
                Create Project
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Project #</th>
                <th>Name</th>
                <th>Client</th>
                <th>Status</th>
                <th>Completion</th>
                <th>Equipment</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="font-medium text-accent">{project.projectNumber}</td>
                  <td className="font-medium">{project.name}</td>
                  <td className="text-secondary-color">{project.client}</td>
                  <td><StatusBadge status={project.status} /></td>
                  <td style={{ minWidth: '140px' }}>
                    <ProgressBar value={project.completionPercentage} size="sm" />
                  </td>
                  <td className="text-secondary-color">{project.equipmentCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && createPortal(
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Project</h2>
              <button className="modal-close" onClick={() => setShowCreate(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="required">Project Number</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={formData.projectNumber}
                      onChange={(e) => setFormData({ ...formData, projectNumber: e.target.value })}
                      placeholder="e.g., CX-2026-001"
                    />
                  </div>
                  <div className="form-group">
                    <label className="required">Project Name</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Project name"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="required">Client</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={formData.client}
                      onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                      placeholder="Client name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Project location"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as ProjectStatus })}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Active">Active</option>
                      <option value="OnHold">On Hold</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="required">Start Date</label>
                    <input
                      type="date"
                      className="form-input"
                      required
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-textarea"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Project description..."
                    rows={3}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>, document.body
      )}
    </div>
  )
}
